<template>
  <div>
    <loader v-if="isLoading" />
    <template v-else>
      <div class="card-container d-flex flex-wrap mb-3" :key="refreshProgress">
        <div v-for="progress in progressData" class="m-2">
          <div class="card h-100 justify-content-start">
            <div class="card-header fw-bold">
              {{ progress.name }}
            </div>
            <div class="card-content d-flex flex-wrap p-3">
              <el-progress type="circle" :percentage="progress.progress" :color="colors"></el-progress>
            </div>
            <div class="card-footer">
              <div class="p-1">
                {{ `${progress.currentCount} / ${progress.instanceCount} task_executed` }}
              </div>
              <div class="p-1">{{ `success: ${progress.successCount}` }}</div>
              <div class="p-1">{{ `failed: ${progress.failedCount}` }}</div>
            </div>
          </div>
        </div>
      </div>
      <queue-list-container ref="list-container" v-bind="$props" :process-data="listData" @storage-key="setStorage" />
    </template>
  </div>
</template>

<script>
import Loader from '../Loader'
import { get, pickBy } from 'lodash'
import EventBus from '../../utils/event-bus'
import { schema } from './process-queue-list'
import { createStorage } from '../../utils'

export default {
  name: 'ProcessQueueList',
  components: {
    Loader,
    QueueListContainer: () => import('../list-new/ListContainer'),
  },
  props: {
    panelId: {
      type: String,
      default: '',
    },
    extraOptions: {
      type: Object,
      default: () => ({}),
    },
    customLayout: {
      type: String,
      default: '<list-view-container/>',
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    searchData: {
      type: Object,
      default: () => ({}),
    },
    hiddenSearchData: {
      type: Object,
      default: () => ({}),
    },
    controllerName: {
      type: String,
      default: () => {
        return window.CORE.requestController
      },
    },
  },
  data() {
    return {
      isLoading: true,
      offset: 0,
      limit: 10,
      search: {},
      order: {},
      storageKey: null,
      restoreOffset: false,
      refreshList: 0,
      recordsTotal: 0,
      progressCount: 0,
      refreshProgress: 0,
      recordsFiltered: 0,
      processQueueData: [],
      colors: [
        { color: '#f56c6c', percentage: 20 },
        { color: '#e6a23c', percentage: 40 },
        { color: '#5cb87a', percentage: 60 },
        { color: '#1989fa', percentage: 80 },
        { color: '#6f7ad3', percentage: 100 },
      ],
      progress: [],
    }
  },
  computed: {
    id() {
      return this.$get('id')
    },
    listData: {
      set(value) {
        this.restoreOffset = value.restoreOffset
        this.processQueueData = value.items
        this.recordsTotal = get(value, 'meta.totalItems', 0)
        this.recordsFiltered = get(value, 'meta.itemCount', 0)
      },
      get() {
        return {
          setup: schema,
          list: {
            data: this.processQueueData,
            restoreOffset: this.restoreOffset,
            recordsTotal: this.recordsTotal,
            recordsFiltered: this.recordsFiltered,
          },
        }
      },
    },
    progressData() {
      return this.progress
    },
  },
  watch: {
    async progressCount(newValue, oldValue) {
      if (newValue !== oldValue) {
        await this.getData()
      }
    },
  },
  methods: {
    filterSearchParam(search) {
      return pickBy(search, (value, key) => key !== 'start')
    },
    getSearchData() {
      const search = get(this.$refs['list-container'], 'searchParams', {})
      return this.filterSearchParam(search)
    },
    convertOrder(order) {
      return order.reduce((acc, curr) => {
        return Object.assign(acc, {
          [curr.column]: curr.dir,
        })
      }, {})
    },
    getOrderParams() {
      const order = get(this.$refs['list-container'], 'orderParams', [])
      return this.convertOrder(order)
    },
    mapResults(items) {
      return items.map((item) => {
        item.currentCount = get(item, 'successCount', 0) + get(item, 'failedCount', 0)
        item.progress =
          item.status === 'processing' ? Math.round((item.currentCount / get(item, 'instanceCount', 1)) * 100) : null
        return item
      })
    },
    async setStorage(key) {
      this.storageKey = key
      if (this.storageKey) {
        const panelIndex = get(this.panelId.match(/\d+$/), '0', 0)
        this.localStore = createStorage(this.storageKey, { id: panelIndex })

        let storedItem = await this.localStore.getStoreContent()
        if (storedItem) await this.filterList(storedItem)
      }
    },
    async filterList(storedItem) {
      this.order = this.convertOrder(get(storedItem, 'orderParams', []))
      this.search = this.filterSearchParam(get(storedItem, 'searchParams', {}))
      await this.getData(true)
    },
    async getQueue(filter = {}, order = {}, page = 1) {
      return (
        await this.$client.execute({
          operationId: 'ProcessQueue_get',
          parameters: {
            search: JSON.stringify(filter),
            order: JSON.stringify(order),
            page,
            limit: this.limit,
          },
        })
      ).body
    },
    async applyFilter(event) {
      if (event.id !== this.panelId) return

      let resetPagination = event.type !== 'pagination'
      this.search = this.getSearchData()
      this.order = this.getOrderParams()

      await this.getData(resetPagination)
    },
    async setPager(event) {
      this.limit = get(event, 'pager.limit', this.pageSize)
      this.offset = get(event, 'pager.offset', 0)
    },
    async getProgress() {
      const filter = { status: 'processing' }
      const items = (await this.getQueue(filter)).items || []
      this.progressCount = items.length
      this.progress = this.mapResults(items)
      this.refreshProgress++
    },
    async getData(resetPagination = false) {
      const filter = {
        ...this.searchData,
        ...this.search,
      }
      const page = resetPagination ? 1 : this.offset / this.limit + 1

      const result = await this.getQueue(filter, this.order, page)

      this.listData = { ...result, restoreOffset: resetPagination }
    },
  },
  async mounted() {
    this.limit = this.pageSize

    await this.getData()
    await this.getProgress()

    const queuePollInterval = get(window, 'CORE.env.queuePollInterval', 10000)

    const poll = function () {
      this.getProgress()
      setTimeout(poll, queuePollInterval)
    }.bind(this)
    setTimeout(poll, queuePollInterval)

    EventBus.$on('set-pager', this.setPager)
    EventBus.$on(`apply-filter-${this.id}`, this.applyFilter)

    this.isLoading = false
  },
  beforeDestroy() {
    EventBus.$off('set-pager', this.setPager)
    EventBus.$off(`apply-filter-${this.id}`, this.applyFilter)
  }
}
</script>
